(() => {
  if (window.__velzeenReelsLoadedV3) return;
  window.__velzeenReelsLoadedV3 = true;

  const SECTION_SELECTOR = '[data-vz-reels-section]';
  const PLAYER_SELECTOR = '[data-vz-reels-player]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 989px)');
  const precisePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function loadVideo(player, preload = 'auto') {
    const video = player?.querySelector('.vz-reels-video');
    const source = video?.querySelector('source[data-src]');
    if (!video || !source) return video;
    video.preload = preload;
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
    video.load();
    return video;
  }

  function initialiseSection(section) {
    if (!section || section.dataset.vzReelsReady === 'true') return;
    section.dataset.vzReelsReady = 'true';

    const track = section.querySelector('[data-vz-reels-track]');
    const previousButton = section.querySelector('[data-vz-reels-prev]');
    const nextButton = section.querySelector('[data-vz-reels-next]');
    const players = Array.from(section.querySelectorAll(PLAYER_SELECTOR));
    const visibility = new Map();
    let activePlayer = null;
    let hoveredPlayer = null;
    let updateFrame = 0;

    function stop(player, clearManual = true) {
      const video = player?.querySelector('.vz-reels-video');
      if (!video) return;
      video.pause();
      video.controls = false;
      video.muted = true;
      video.loop = true;
      player.classList.remove('is-playing', 'is-autoplaying');
      if (clearManual) player.dataset.vzManual = 'false';
      if (activePlayer === player) activePlayer = null;
    }

    function pauseOthers(player) {
      players.forEach((candidate) => {
        if (candidate !== player) stop(candidate);
      });
    }

    function play(player, manual = false) {
      const video = loadVideo(player);
      if (!video) return;
      pauseOthers(player);
      activePlayer = player;
      player.dataset.vzManual = manual ? 'true' : 'false';
      player.classList.add('is-playing');
      player.classList.toggle('is-autoplaying', !manual);
      video.controls = manual;
      video.muted = !manual;
      video.loop = !manual;
      video.play().catch(() => {
        if (!manual) stop(player, false);
      });
    }

    function bestVisiblePlayer() {
      let best = null;
      let bestRatio = 0;
      players.forEach((player) => {
        const ratio = visibility.get(player) || 0;
        if (ratio > bestRatio) {
          best = player;
          bestRatio = ratio;
        }
      });
      return bestRatio >= (mobileViewport.matches ? 0.55 : 0.35) ? best : null;
    }

    function updatePlayback() {
      updateFrame = 0;
      if (document.hidden || reducedMotion.matches) {
        if (activePlayer?.dataset.vzManual !== 'true') stop(activePlayer);
        return;
      }
      if (activePlayer?.dataset.vzManual === 'true') return;
      const candidate = !mobileViewport.matches && hoveredPlayer ? hoveredPlayer : bestVisiblePlayer();
      if (!candidate) {
        stop(activePlayer);
      } else if (candidate !== activePlayer) {
        play(candidate);
      }
    }

    function schedulePlayback() {
      if (!updateFrame) updateFrame = window.requestAnimationFrame(updatePlayback);
    }

    function updateArrows() {
      if (!track || !previousButton || !nextButton) return;
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const overflowing = maximum > 2;
      previousButton.hidden = !overflowing || track.scrollLeft <= 2;
      nextButton.hidden = !overflowing || track.scrollLeft >= maximum - 2;
    }

    function scrollTrack(direction) {
      const firstCard = track?.querySelector('.vz-reels-card');
      if (!track || !firstCard) return;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const cardStep = firstCard.getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.floor((track.clientWidth + gap) / cardStep));
      track.scrollBy({
        left: direction * cardStep * Math.max(1, visibleCards - 1),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    previousButton?.addEventListener('click', () => scrollTrack(-1));
    nextButton?.addEventListener('click', () => scrollTrack(1));
    track?.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });
    if ('ResizeObserver' in window && track) {
      const resizeObserver = new ResizeObserver(updateArrows);
      resizeObserver.observe(track);
    }

    players.forEach((player) => {
      const video = player.querySelector('.vz-reels-video');
      const button = player.querySelector('.vz-reels-play-button');
      if (!video || !button) return;
      const markReady = () => player.classList.add('is-ready');
      if (video.readyState >= 2) {
        markReady();
      } else {
        video.addEventListener('loadeddata', markReady, { once: true });
      }
      button.addEventListener('click', () => play(player, true));
      video.addEventListener('click', () => {
        if (video.paused || video.muted || !video.controls) play(player, true);
      });
      video.addEventListener('ended', () => {
        stop(player);
        schedulePlayback();
      });
      player.addEventListener('pointerenter', () => {
        if (mobileViewport.matches || !precisePointer.matches) return;
        hoveredPlayer = player;
        schedulePlayback();
      });
      player.addEventListener('pointerleave', () => {
        if (hoveredPlayer === player) hoveredPlayer = null;
        if (player.dataset.vzManual !== 'true') schedulePlayback();
      });
    });

    function primePlayer(player) {
      const video = player.querySelector('.vz-reels-video');
      if (!video || video.readyState >= 2) return Promise.resolve();
      loadVideo(player, 'auto');
      return new Promise((resolve) => {
        const finish = () => {
          if (player.dataset.vzManual !== 'true' && activePlayer !== player) {
            video.pause();
            video.preload = 'metadata';
          }
          resolve();
        };
        video.addEventListener('loadeddata', finish, { once: true });
        video.addEventListener('error', finish, { once: true });
      });
    }

    async function primePlayers() {
      if (section.dataset.vzReelsPriming === 'true') return;
      section.dataset.vzReelsPriming = 'true';
      for (const player of players) {
        await primePlayer(player);
        await new Promise((resolve) => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(resolve, { timeout: 1200 });
          } else {
            window.setTimeout(resolve, 80);
          }
        });
      }
    }

    function primeAfterPageLoad() {
      if ('IntersectionObserver' in window) {
        const sectionObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          sectionObserver.disconnect();
          primePlayers();
        }, { rootMargin: '1000px 0px', threshold: 0.01 });
        sectionObserver.observe(section);
      } else {
        primePlayers();
      }
    }

    if ('IntersectionObserver' in window) {
      const playbackObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => visibility.set(entry.target, entry.intersectionRatio));
        schedulePlayback();
      }, { threshold: [0, 0.35, 0.55, 0.75, 1] });
      players.forEach((player) => playbackObserver.observe(player));
    } else if (players[0]) {
      loadVideo(players[0]);
    }

    const handleViewportChange = () => {
      hoveredPlayer = null;
      if (activePlayer?.dataset.vzManual !== 'true') stop(activePlayer);
      updateArrows();
      schedulePlayback();
    };
    if (mobileViewport.addEventListener) {
      mobileViewport.addEventListener('change', handleViewportChange);
      reducedMotion.addEventListener('change', schedulePlayback);
    } else {
      mobileViewport.addListener(handleViewportChange);
      reducedMotion.addListener(schedulePlayback);
    }
    document.addEventListener('visibilitychange', schedulePlayback);
    if (document.readyState === 'complete') {
      primeAfterPageLoad();
    } else {
      window.addEventListener('load', primeAfterPageLoad, { once: true });
    }
    updateArrows();
  }

  function initialiseAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) initialiseSection(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initialiseSection);
  }

  initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
